import { useState, useEffect } from "react"
import { ArrowLeft, Save, Camera, Check, X } from "lucide-react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import PhotoUpload from "@/components/PhotoUpload"
import { PHOTO_BUCKET, createSignedFileUrl, uploadFileToBucket, type UploadablePhoto } from "@/lib/vehicleStorage"

interface Avaliacao {
  id: string
  veiculo_id: string
  status: string
  updated_at?: string
  observacoes_iniciais?: string
  veiculos: {
    placa: string
    marca: string
    modelo: string
    ano: number
    cor: string
    quilometragem: number
  }
}

interface PosicaoAvaliacao {
  posicao: string
  fotos: UploadablePhoto[]
  observacoes: string
  itens: Record<string, boolean>
}

type PosicaoFieldValue = PosicaoAvaliacao[keyof PosicaoAvaliacao]

const POSICOES = [
  { id: "frontal", nome: "Frontal", icone: "🚗" },
  { id: "traseira", nome: "Traseira", icone: "🔙" },
  { id: "lateral_esquerda", nome: "Lateral Esquerda", icone: "⬅️" },
  { id: "lateral_direita", nome: "Lateral Direita", icone: "➡️" },
  { id: "motor", nome: "Motor", icone: "⚙️" },
  { id: "painel", nome: "Painel e Instrumentos", icone: "📊" },
  { id: "interior", nome: "Interior", icone: "💺" },
  { id: "porta_malas", nome: "Porta-malas", icone: "🧳" },
  { id: "detalhes", nome: "Detalhes Extras", icone: "🔍" }
]

const CHECKLISTS = {
  frontal: [
    "Parachoque dianteiro",
    "Grade frontal",
    "Faróis",
    "Capô",
    "Para-brisa",
    "Emblema/marca",
    "Placa dianteira"
  ],
  traseira: [
    "Parachoque traseiro",
    "Lanternas",
    "Tampa do porta-malas",
    "Emblema/marca",
    "Placa traseira",
    "Escapamento"
  ],
  lateral_esquerda: [
    "Portas e maçanetas",
    "Retrovisor",
    "Vidros",
    "Para-lamas",
    "Frisos e adesivos"
  ],
  lateral_direita: [
    "Portas e maçanetas",
    "Retrovisor",
    "Vidros",
    "Para-lamas",
    "Frisos e adesivos"
  ],
  motor: [
    "Compartimento limpo",
    "Bateria",
    "Correias e mangueiras",
    "Filtros",
    "Sinais de vazamento ou ferrugem"
  ],
  painel: [
    "Velocímetro",
    "Luzes de advertência",
    "Sistema multimídia/rádio",
    "Ar-condicionado",
    "Controles"
  ],
  interior: [
    "Bancos",
    "Forros de portas",
    "Tapetes",
    "Volante",
    "Espaço interno geral"
  ],
  porta_malas: [
    "Capacidade e revestimento",
    "Estepe e ferramentas",
    "Sinais de ferrugem ou danos"
  ],
  detalhes: [
    "Estado das rodas",
    "Estado dos pneus",
    "Transparência das luzes",
    "Pintura",
    "Arranhões ou amassados"
  ]
}

const AvaliacaoPosicoes = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [avaliacao, setAvaliacao] = useState<Avaliacao | null>(null)
  const [posicoesAvaliacao, setPosicoesAvaliacao] = useState<Record<string, PosicaoAvaliacao>>({})
  const [posicaoAtual, setPosicaoAtual] = useState("frontal")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchAvaliacao()
    initializePosicoes()
    fetchPosicoesSalvas()
  }, [id])

  const buildInitialPosicoes = () => {
    const inicial: Record<string, PosicaoAvaliacao> = {}
    POSICOES.forEach(posicao => {
      inicial[posicao.id] = {
        posicao: posicao.id,
        fotos: [],
        observacoes: "",
        itens: CHECKLISTS[posicao.id as keyof typeof CHECKLISTS].reduce((acc, item) => {
          acc[item] = false
          return acc
        }, {} as Record<string, boolean>)
      }
    })
    return inicial
  }

  const initializePosicoes = () => {
    setPosicoesAvaliacao(buildInitialPosicoes())
  }

  const normalizeFotos = async (value: unknown): Promise<UploadablePhoto[]> => {
    if (!Array.isArray(value)) return []
    const fotos = value.filter((item): item is string => typeof item === "string")
    return Promise.all(fotos.map(async (item) => ({
      url: (await createSignedFileUrl(PHOTO_BUCKET, item)) ?? item,
      storagePath: item,
    })))
  }

  const normalizeItens = (value: unknown) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {}
    return Object.entries(value as Record<string, unknown>).reduce((acc, [key, item]) => {
      if (typeof item === "boolean") {
        acc[key] = item
      }
      return acc
    }, {} as Record<string, boolean>)
  }

  const fetchPosicoesSalvas = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('avaliacoes_posicoes')
        .select('posicao, fotos, observacoes, itens')
        .eq('avaliacao_id', id)

      if (error) {
        console.error("Erro ao carregar posições:", error)
        return
      }

      if (!data || data.length === 0) {
        return
      }

      const base = buildInitialPosicoes()
      for (const item of data) {
        if (!base[item.posicao]) return
        base[item.posicao] = {
          posicao: item.posicao,
          fotos: await normalizeFotos(item.fotos),
          observacoes: item.observacoes || "",
          itens: {
            ...base[item.posicao].itens,
            ...normalizeItens(item.itens)
          }
        }
      }
      setPosicoesAvaliacao(base)
    } catch (error) {
      console.error("Erro inesperado ao carregar posições:", error)
    }
  }

  const fetchAvaliacao = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('avaliacoes')
        .select(`
          *,
          veiculos:veiculo_id (
            placa,
            marca,
            modelo,
            ano,
            cor,
            quilometragem
          )
        `)
        .eq('id', id)
        .single()

      if (error) {
        toast.error("Erro ao carregar avaliação")
        return
      }
      setAvaliacao(data)
    } catch (error) {
      toast.error("Erro inesperado")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const updatePosicao = (posicaoId: string, campo: keyof PosicaoAvaliacao, valor: PosicaoFieldValue) => {
    setPosicoesAvaliacao(prev => ({
      ...prev,
      [posicaoId]: {
        ...prev[posicaoId],
        [campo]: valor
      }
    }))
  }

  const toggleItem = (posicaoId: string, item: string) => {
    setPosicoesAvaliacao(prev => ({
      ...prev,
      [posicaoId]: {
        ...prev[posicaoId],
        itens: {
          ...prev[posicaoId].itens,
          [item]: !prev[posicaoId].itens[item]
        }
      }
    }))
  }

  const salvarAvaliacao = async () => {
    if (!id) {
      toast.error("ID da avaliação não encontrado")
      return
    }

    setSaving(true)
    try {
      const dadosParaSalvar = await Promise.all(Object.values(posicoesAvaliacao).map(async (posicao) => ({
        avaliacao_id: id,
        posicao: posicao.posicao,
        fotos: await Promise.all(posicao.fotos.map(async (foto) => {
          if (foto.file) {
            const { path } = await uploadFileToBucket(
              PHOTO_BUCKET,
              foto.file,
              `avaliacoes/${id}/posicoes/${posicao.posicao}`,
            )
            return path
          }

          return foto.storagePath ?? foto.url
        })),
        observacoes: posicao.observacoes || null,
        itens: posicao.itens
      })))

      const { error } = await supabase
        .from('avaliacoes_posicoes')
        .upsert(dadosParaSalvar, { onConflict: 'avaliacao_id,posicao' })

      if (error) {
        console.error("Erro ao salvar avaliação:", error)
        toast.error("Erro ao salvar avaliação")
        return
      }

      toast.success("Avaliação salva com sucesso!")
    } catch (error) {
      toast.error("Erro ao salvar avaliação")
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!avaliacao) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Avaliação não encontrada</p>
        <Button asChild className="mt-4">
          <Link to="/avaliacoes">Voltar para Avaliações</Link>
        </Button>
      </div>
    )
  }

  const posicaoData = posicoesAvaliacao[posicaoAtual]
  const checklist = CHECKLISTS[posicaoAtual as keyof typeof CHECKLISTS]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/avaliacoes">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Avaliação por Posições</h1>
          <p className="text-muted-foreground">
            {avaliacao.veiculos.placa} - {avaliacao.veiculos.marca} {avaliacao.veiculos.modelo}
          </p>
        </div>
        <Button onClick={salvarAvaliacao} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Salvando..." : "Salvar Avaliação"}
        </Button>
      </div>

      {/* Abas de Posições */}
      <Tabs value={posicaoAtual} onValueChange={setPosicaoAtual} className="w-full">
        <TabsList className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 mb-6 gap-2">
          {POSICOES.map((posicao) => (
            <TabsTrigger key={posicao.id} value={posicao.id} className="flex flex-col items-center h-16">
              <span className="text-lg">{posicao.icone}</span>
              <span className="text-xs mt-1">{posicao.nome}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {POSICOES.map((posicao) => (
          <TabsContent key={posicao.id} value={posicao.id} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload de Fotos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Fotos da {posicao.nome}
                  </CardTitle>
                  <CardDescription>
                    Adicione fotos de diferentes ângulos desta posição
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PhotoUpload
                    photos={posicoesAvaliacao[posicao.id]?.fotos || []}
                    onPhotosUpdate={(newPhotos) => updatePosicao(posicao.id, 'fotos', newPhotos)}
                    maxPhotos={10}
                  />
                </CardContent>
              </Card>

              {/* Checklist */}
              <Card>
                <CardHeader>
                  <CardTitle>Checklist Visual</CardTitle>
                  <CardDescription>
                    Marque os itens que aparecem nas fotos
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3">
                    {checklist.map((item) => (
                      <div key={item} className="flex items-center space-x-2">
                        <Checkbox
                          id={`${posicao.id}-${item}`}
                          checked={posicoesAvaliacao[posicao.id]?.itens[item] || false}
                          onCheckedChange={() => toggleItem(posicao.id, item)}
                        />
                        <Label htmlFor={`${posicao.id}-${item}`} className="text-sm">
                          {item}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Observações */}
            <Card>
              <CardHeader>
                <CardTitle>Observações da {posicao.nome}</CardTitle>
                <CardDescription>
                  Adicione observações específicas sobre esta posição
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Descreva detalhes, problemas ou observações específicas desta posição..."
                  value={posicoesAvaliacao[posicao.id]?.observacoes || ""}
                  onChange={(e) => updatePosicao(posicao.id, 'observacoes', e.target.value)}
                  className="min-h-[120px]"
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

export default AvaliacaoPosicoes
